import React, { useEffect } from 'react';

import { siteUrl } from '@utils/siteConfig';
import { DefaultLayout } from '@layouts/DefaultLayout';
import { Consulting } from '@layouts/Consulting';
import { keysToCamel } from '@utils/snakeCaseParser';
import { CustomHelmet } from '@components/CustomHelmet/CustomHelmet';
import { DetailedCaseStudy } from '@layouts/CaseStudies/components';
import { GroupedCaseStudy } from '@layouts/CaseStudies/types';

const lookupMap = {
	challenge: 'challenge',
	result: 'result',
	key: 'keyFeatures',
};

const getCategoryNameByKey = (key: string) => {
	const leadingFieldName = key.split('_')[0];
	return lookupMap[leadingFieldName] || 'general';
};

const sanitizeKeyName = (key: string) => {
	const splittedKey = key.split('_');
	const leadingFieldName = splittedKey[0];
	if (lookupMap[leadingFieldName]) {
		splittedKey.shift();
		return splittedKey.join('_');
	}
	return key;
};

const CaseStudy: React.FC<{ pageContext: any }> = ({ pageContext }) => {
	const caseStudyData = pageContext;
	const title = `${caseStudyData.title} | EMBIQ`;
	const description = `Solutions for business, involving locations, positioning, or asset tracking. Every project is based on the very best and the latest technologies and tools.`;

	const preparedCaseStudyData = Object.entries(caseStudyData).reduce(
		(prev, current) => {
			const key = current[0];
			const value: any = current[1];
			const category = getCategoryNameByKey(key);
			const sanitizedKey = sanitizeKeyName(key);
			const isImageObject = value && key.endsWith('image');

			return {
				...prev,
				[category]: {
					...prev[category],
					[sanitizedKey]: isImageObject ? value.imageFile.publicURL : value,
				},
			};
		},
		{
			general: {},
			challenge: {},
			result: {},
			keyFeatures: {},
		}
	);

	const parsedCaseStudyData: GroupedCaseStudy = keysToCamel(
		preparedCaseStudyData
	);

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	return (
		<>
			<CustomHelmet
				title={title}
				ogTitle={title}
				metaDescription={description}
				ogDescription={description}
				ogImage={`${siteUrl}${parsedCaseStudyData.result.image}`}
			/>
			<DefaultLayout scheme="light">
				{parsedCaseStudyData && <DetailedCaseStudy {...parsedCaseStudyData} />}
				<Consulting />
			</DefaultLayout>
		</>
	);
};

export default CaseStudy;
